import Backbone from 'backbone';
import React from 'react';
import _ from 'underscore';

import {assert} from 'Asserts';
import {StandardFeature} from 'auth/capabilities/CapabilityConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {StringIDFolder} from 'constants/FolderConstants';
import {REGISTRY_TYPE} from 'constants/SourceConstants';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import {BasicInformationPanelState} from 'FileStates';
import BaseFolderItem from 'folder-items/BaseFolderItem';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {Review} from 'notebook/reviews/ReviewConstants';
import notifications from 'notifications';
import * as popup from 'Popup';
import * as RegistryUtil from 'registry/RegistryUtil';
import {capitalize, capwords, typeToName} from 'util/NameUtil';

import ReactPanelBaseView, {getTabAndPanelIds, Subpanel} from '../ReactPanelBaseView';
import InformationSubpanel from './InformationSubpanel';
import TagsSubpanel from './TagsSubpanel';

export interface DownloadInfo {
  url: string;
  title: string;
}

// A handful of optional "hooks" that specific subclasses may implement, e.g. Entry supports
// review() and requestExport(), while AaSequence supports getDownloadInfo(). None of these are
// part of BaseFolderItem/BasicInformationPanelView, so they're accessed dynamically via a cast
// rather than declared on the class, letting each subclass implement only what it supports.
interface OptionalOverrides {
  getAdditionalPanelContent?: () => React.ReactNode;
  getDownloadInfo?: () => DownloadInfo | Array<DownloadInfo>;
  requestExport?: () => unknown;
  showPatentsafeExport?: boolean;
}

interface ReviewableFolderItem {
  review?: () => Review;
}

class BasicInformationPanelView<T extends BaseFolderItem> extends ReactPanelBaseView<
  BasicInformationPanelState,
  T
> {
  friendlyFileType(): string {
    return typeToName(this.model.fileState().fileType());
  }

  folderDefaultsKey(): FolderDefaultsKey {
    return FolderDefaultsKey.INVENTORY;
  }

  override initialize(options?: Backbone.ViewOptions<T>): void {
    super.initialize();
    this.deleteModal = this.deleteModal.bind(this);
    this.deleteModel = this.deleteModel.bind(this);
    this._saveModelIfNeededAndNotifySuccess = this._saveModelIfNeededAndNotifySuccess.bind(this);
    this.saveChangesToModel = this.saveChangesToModel.bind(this);
    this.saveModel = this.saveModel.bind(this);
    this.onSaveSuccess = this.onSaveSuccess.bind(this);
    this.onDeleteSuccess = this.onDeleteSuccess.bind(this);
  }

  deleteModal(): void {
    new popup.Confirm({
      title: `Delete ${capwords(this.friendlyFileType())}`,
      msg: `Are you sure you want to delete the ${this.friendlyFileType()}? It cannot be recovered.`,
    })
      .choice('Cancel', 'btn-af-default')
      // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in initialize()
      .choice('Delete', 'btn-af-danger', this.deleteModel)
      .show();
  }

  deleteModel(): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in initialize()
    this.model.destroy().fail(notifications.notifyError).done(this.onDeleteSuccess);
  }

  _saveModelIfNeededAndNotifySuccess(data: object): void {
    if (_.isEmpty(data)) {
      this.onSaveSuccess();
    } else {
      this.saveModel(data);
    }
  }

  async saveChangesToModel(
    name: string | null,
    folderId: string | null,
    tagSchemaId: string | null
  ): Promise<void> {
    const {writable, movable} = this.getPermissions();

    const fileId = this.model.id;
    const oldName = this.model.name() ?? null;
    const oldFolderId = this.model.folder()?.api_identifier ?? null;
    const oldTagSchemaId = this.model.tagSchema()?.id ?? null;

    const data: {tagSchemaId?: string | null; folder_id?: string | null} = {};
    if (oldTagSchemaId !== tagSchemaId) {
      data.tagSchemaId = tagSchemaId;
    }

    if (oldName !== name) {
      assert(writable, 'User attempted to rename a file without authorized WRITE');
      assert(name != null, 'Cannot rename item to a null name');
      try {
        await FileBrowserActions.renameFileWithDataMods(fileId, this.isRegistered(), {
          oldName: oldName ?? undefined,
          newName: name,
          notifyOnSuccess: true,
        });
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (err) {
        notifications.notifyError(err);
        return;
      }
    }

    if (oldFolderId !== folderId) {
      assert(Boolean(movable), 'User attempted to move a file without authorized MOVE');
      assert(folderId != null, 'Cannot move item to a null folder');
      data.folder_id = folderId;
      try {
        await FileBrowserActions.moveItemsWithDataMods([fileId], folderId, {
          parentFolderIds: [oldFolderId!],
          hasRegisteredItems: this.isRegistered(),
        });
        // eslint-disable-next-line benchlint/always-throw-from-catch
      } catch (err) {
        notifications.notifyError(err);
        return;
      }
    }

    this._saveModelIfNeededAndNotifySuccess(data);
  }

  // Saves the data to the model and calls onSaveSuccess in case of success or displays
  // the error in case of an error
  saveModel(data: object): void {
    // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in initialize()
    this.model.save(data, {patch: true, wait: true}).fail(notifications.notifyError).done(this.onSaveSuccess);
  }

  onSaveSuccess(): void {
    notifications.success(`${capitalize(this.friendlyFileType())} successfully updated.`);
    if (this.model.fileState().isFolderItem()) {
      if (this.model.folder() != null && this.model.folder()!.source!.sourceType !== REGISTRY_TYPE) {
        FolderDefaults.set(this.folderDefaultsKey(), this.model.folder()!.api_identifier);
      }
    }
    this.render();
  }

  onDeleteSuccess(): void {
    notifications.success(`${capitalize(this.friendlyFileType())} successfully deleted.`);
    FileBrowserActions.removeFromWorkspace(this.model.id);
    if (this.model.fileState().isFolderItem()) {
      FolderDAOCache.invalidateFolderContents(this.model.folder()!.api_identifier);
    }
  }

  getPermissions(): Permissions {
    return this.model.permissions();
  }

  isRegistered(): boolean {
    return this.model.folder() != null && RegistryUtil.isRegistry(this.model.folder()!.source);
  }

  private getReview(): Review | undefined {
    const model = this.model as unknown as ReviewableFolderItem;
    return typeof model.review === 'function' ? model.review() : undefined;
  }

  // Subclasses (e.g. StageEntryInformationPanelView) implement these members directly on
  // themselves rather than through this base class; see the comment on `OptionalOverrides`.
  private getOptionalOverrides(): OptionalOverrides {
    return this as unknown as OptionalOverrides;
  }

  private getThirdPartyStorageServiceData():
    | {
        isSendingToPatentsafe?: boolean;
        lastSentTimestamp?: string;
        lastSentUser?: {handle: string};
      }
    | undefined {
    if (typeof this.model.thirdPartyStorageServiceData !== 'function') {
      return undefined;
    }
    const data = this.model.thirdPartyStorageServiceData();
    if (data == null) {
      return undefined;
    }
    return {
      isSendingToPatentsafe: data.isSendingToPatentsafe,
      lastSentTimestamp: data.lastSentTimestamp ?? undefined,
      lastSentUser: data.lastSentUser ?? undefined,
    };
  }

  getSubpanels(): Array<Subpanel> {
    const overrides = this.getOptionalOverrides();
    return _.compact([
      {
        ...getTabAndPanelIds('information'),
        title: 'Information',
        component: (
          <InformationSubpanel
            additionalPanelContent={
              // Call this as `overrides.getAdditionalPanelContent()`, not via a destructured
              // reference, so that subclass implementations relying on `this` (e.g.
              // StageEntryInformationPanelView) keep working.
              typeof overrides.getAdditionalPanelContent === 'function'
                ? overrides.getAdditionalPanelContent()
                : undefined
            }
            archiveRecord={this.model.archiveRecord()}
            name={this.model.name()}
            folder={this.model.folder() as StringIDFolder | undefined}
            fileType={this.model.fileState().fileType()}
            friendlyFileType={this.friendlyFileType()}
            fileId={this.model.id}
            // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in ReactPanelBaseView.initialize()
            onClose={this.closePanel}
            permissions={this.getPermissions()}
            review={this.getReview()}
            // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in initialize()
            saveChangesToModel={this.saveChangesToModel}
            requestExport={overrides.requestExport}
            // Should return an object with fields "URL" and "title" if downloading is enabled.
            getDownloadInfo={overrides.getDownloadInfo}
            // eslint-disable-next-line @typescript-eslint/unbound-method -- rebound in initialize()
            deleteModel={BenchlingConfig.ENABLE_DELETE ? this.deleteModal : undefined}
            showPatentsafeExport={overrides.showPatentsafeExport ?? false}
            thirdPartyStorageServiceData={this.getThirdPartyStorageServiceData()}
            currentTagSchema={this.model.tagSchema()}
          />
        ),
      },
      this.model.fileState().isFolderItem()
        ? {
            ...getTabAndPanelIds('tags'),
            title: 'Tags',
            component: (
              <TagsSubpanel
                id={this.model.id}
                allowEdits={
                  this.model.allowEdits() &&
                  isFeatureEnabled({
                    feature: StandardFeature.EDIT,
                    item: {id: this.model.id},
                    itemState: {isRegistered: this.isRegistered()},
                  })
                }
                tags={this.model.tags()}
                isRegistered={this.isRegistered()}
              />
            ),
          }
        : undefined,
    ]);
  }

  // To enable testing of subcomponents
  static _InformationSubpanel = InformationSubpanel;
}

export default BasicInformationPanelView;
